// ** Polyfills para compatibilidad con Node.js
import './polyfills';

// ** React Imports
import { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import './globals';

// ** Redux Imports
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from './redux/storeConfig/store';

// ** Intl, CASL & ThemeColors Context
import ability from './configs/acl/ability';
import { AbilityContext } from './utility/context/Can';
import { IntlProviderWrapper } from './utility/context/Internationalization';

// ** Toast & ThemeColors Context
import { ToastContainer } from 'react-toastify';
import { ThemeContext } from './utility/context/ThemeColors';

// ** Spinner (Splash Screen)
import Spinner from './@core/components/spinner/Fallback-spinner';

// ** Error Boundary
import ErrorBoundary from './@core/components/error-boundary';

// ** Reload Prompt (PWA update detection)
import { ReloadPrompt } from './components/ReloadPrompt';

// ** Ripple Button
import './@core/components/ripple-button';

// ** PrismJS
import 'prismjs';
import 'prismjs/components/prism-jsx.min';
import 'prismjs/themes/prism-tomorrow.css';

// ** React Perfect Scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css';

// ** Dockview React
import 'dockview-react/dist/styles/dockview.css';

//** React-Bootstrap **
import 'bootstrap/dist/css/bootstrap.min.css';

// ** React Toastify
import '@styles/react/libs/toastify/toastify.scss';

// ** Core styles
import './@core/assets/fonts/feather/iconfont.css';
import './@core/scss/core.scss';
import './assets/scss/style.scss';


// ** Syncfusion
import { registerLicense } from '@syncfusion/ej2-base';
import { env } from './env';
import TestApp from './test';

// Registering Syncfusion license key
registerLicense(
  'Mgo+DSMBPh8sVXJ9S0d+X1JPd11dXmJWd1p/THNYflR1fV9DaUwxOX1dQl9mSXhSfkdgWHpfdXdQQGhXU00=;ORg4AjUWIQA/Gnt2XFhhQlJHfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTH5Vd01hWn5bcXFWTmhdWkZ/;Mgo+DSMBMAY9C3t2XFhhQlJHfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTH5Vd01hWn5bcXFRR2lUWkZ/'
);

// ** Lazy load app
const LazyApp = lazy(() => import('./router/Router'));

// Cambiar LazyApp por TestApp para pruebas
const AppToRender = LazyApp; //TestApp; // Cambia a LazyApp cuando termines las pruebas

// Aquí defines las variables dinámicas
const basePath = env.VITE_PUBLIC_PATH || '/';
const primaryColor = env.VITE_PRIMARY_COLOR || '#040EFF';

document.documentElement.style.setProperty('--base-path', basePath);
document.documentElement.style.setProperty('--primary-color', primaryColor);

// replace console.* for disable log on production
// VITE_ENABLE_CONSOLE puede ser: string "1"/"0" (desde .env) o número 1/0 (desde env.js)
const enableConsole = Boolean(Number(env.VITE_ENABLE_CONSOLE));
if (env.PROD && !enableConsole) {
  console.debug('🔒 Production mode detected, disabling console logs');
  console.warn = () => {};
  console.info = () => {};
  console.trace = () => {};
  console.log = () => {};
  console.error = () => {};
  console.debug = () => {};
}

// Función para renderizar la aplicación con mejor manejo de errores
async function renderApp() {
  try {
    console.log('🚀 Iniciando renderizado de la aplicación...');

    const container = document.getElementById('root');
    if (!container) {
      console.error('❌ Root element not found');
      document.body.innerHTML =
        '<div style="padding: 20px; color: red; font-family: Arial;">Error: No se pudo encontrar el elemento #root</div>';
      return;
    }

    console.log('✅ Root element found, creating React root...');

    // Esperar a que los polyfills se carguen completamente
    await new Promise((resolve) => setTimeout(resolve, 100));

    const root = createRoot(container);

    console.log('✅ React root created, rendering app...');

    root.render(
      <ErrorBoundary maxErrors={10}>
        <Provider store={store}>
          <PersistGate persistor={persistor}>
            <Suspense fallback={<Spinner />}>
              <AbilityContext.Provider value={ability}>
                <ThemeContext>
                  <IntlProviderWrapper>
                    <AppToRender />
                    <ReloadPrompt />
                    <ToastContainer
                      position="top-right"
                      autoClose={5000}
                      hideProgressBar={false}
                      newestOnTop
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                    />
                  </IntlProviderWrapper>
                </ThemeContext>
              </AbilityContext.Provider>
            </Suspense>
          </PersistGate>
        </Provider>
      </ErrorBoundary>
    );

    console.log('✅ Aplicación renderizada exitosamente');
  } catch (error) {
    console.error('❌ Error crítico en renderizado de la aplicación:', error);

    // Fallback: mostrar error en pantalla
    const container = document.getElementById('root');
    if (container) {
      container.innerHTML = `
        <div style="padding: 20px; color: red; font-family: Arial; background: #fff;">
          <h2>Error de inicialización</h2>
          <p>La aplicación no pudo iniciarse correctamente.</p>
          <details>
            <summary>Detalles del error</summary>
            <pre style="background: #f5f5f5; padding: 10px; margin-top: 10px; white-space: pre-wrap;">${
              error instanceof Error ? error.message : String(error)
            }\n\n${error instanceof Error ? error.stack : ''}</pre>
          </details>
          <button onclick="location.reload()" style="background: #007bff; color: white; border: none; padding: 10px 20px; margin-top: 10px; cursor: pointer;">
            Recargar página
          </button>
        </div>
      `;
    }
  }
}

// Iniciar la aplicación cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderApp);
} else {
  renderApp();
}

